import { buildPublicSiteUrl } from './publicSiteUrl';

const PENDING_TICKET_CODE_KEY = 'lockedin_pending_ticket_code';
const PENDING_TICKET_PATH_KEY = 'lockedin_pending_ticket_path';
const PENDING_TICKET_LS_KEY = 'lockedin_pending_ticket_ls';
const PENDING_TICKET_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function isTicketPath(pathname = '') {
  return String(pathname || '').startsWith('/ticket/');
}

function parseTicketCodeFromPath(pathname) {
  const match = String(pathname || '').match(/^\/ticket\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]).toUpperCase() : null;
}

export function logTicketResume(message, detail) {
  if (typeof window === 'undefined') return;
  if (!isTicketPath(window.location.pathname) && !peekPendingTicketCode()) return;
  if (detail !== undefined) {
    console.log(`[ticket-resume] ${message}`, detail);
  } else {
    console.log(`[ticket-resume] ${message}`);
  }
}

export function setPendingTicketCode(code, { path, log = true } = {}) {
  if (!code || typeof window === 'undefined') return;
  const normalized = String(code).trim().toUpperCase();
  try {
    sessionStorage.setItem(PENDING_TICKET_CODE_KEY, normalized);
    const ticketPath = path || `/ticket/${encodeURIComponent(normalized)}`;
    sessionStorage.setItem(PENDING_TICKET_PATH_KEY, ticketPath);
    localStorage.setItem(PENDING_TICKET_LS_KEY, JSON.stringify({
      code: normalized,
      path: ticketPath,
      at: Date.now(),
    }));
    if (log) logTicketResume('captured code', normalized);
  } catch (_) {
    /* ignore */
  }
}

export function capturePendingTicketFromPath(pathname) {
  const code = parseTicketCodeFromPath(pathname);
  if (!code) return null;
  const pathOnly = String(pathname || '').split('?')[0] || `/ticket/${encodeURIComponent(code)}`;
  setPendingTicketCode(code, { path: pathOnly });
  return code;
}

export function peekPendingTicketCode() {
  if (typeof window === 'undefined') return null;
  try {
    const fromSession = sessionStorage.getItem(PENDING_TICKET_CODE_KEY);
    if (fromSession) return fromSession;

    const raw = localStorage.getItem(PENDING_TICKET_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.code) return null;
    if (Date.now() - (parsed.at || 0) > PENDING_TICKET_TTL_MS) {
      localStorage.removeItem(PENDING_TICKET_LS_KEY);
      return null;
    }
    sessionStorage.setItem(PENDING_TICKET_CODE_KEY, parsed.code);
    if (parsed.path) {
      sessionStorage.setItem(PENDING_TICKET_PATH_KEY, parsed.path);
    }
    return parsed.code;
  } catch (_) {
    return null;
  }
}

export function peekPendingTicketPath() {
  if (typeof window === 'undefined') return null;
  try {
    const fromSession = sessionStorage.getItem(PENDING_TICKET_PATH_KEY);
    if (fromSession) return fromSession;
    const code = peekPendingTicketCode();
    return code ? `/ticket/${encodeURIComponent(code)}` : null;
  } catch (_) {
    return null;
  }
}

export function buildPendingTicketResumePath(code) {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) return '/ticket/';
  return `/ticket/${encodeURIComponent(normalized)}?resume=1`;
}

export function clearPendingTicketIntent(reason = 'cleared') {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(PENDING_TICKET_CODE_KEY);
    sessionStorage.removeItem(PENDING_TICKET_PATH_KEY);
    localStorage.removeItem(PENDING_TICKET_LS_KEY);
    logTicketResume('cleared pending ticket', reason);
  } catch (_) {
    /* ignore */
  }
}

/** @deprecated Use peekPendingTicketCode + clearPendingTicketIntent */
export function consumePendingTicketCode() {
  const value = peekPendingTicketCode();
  clearPendingTicketIntent('consumePendingTicketCode');
  return value;
}

export function buildChallengeTicketUrl(ticketCode) {
  if (!ticketCode) return '/ticket/';
  return buildPublicSiteUrl(`/ticket/${encodeURIComponent(String(ticketCode).toUpperCase())}`);
}

export function buildChallengeTicketShareText({
  creatorName = 'Someone',
  teamA,
  teamB,
  amount,
  url,
}) {
  const matchup = teamA && teamB ? `${teamA} vs ${teamB}` : 'Head-to-head challenge';
  const stake = amount != null ? `$${Number(amount).toFixed(0)}` : '';
  return `${creatorName} sent you a LockedIn Challenge Ticket:\n${matchup}${stake ? ` · ${stake}` : ''}\nOpen Ticket: ${url}`;
}
